import { useEffect, useState, Fragment, ChangeEvent } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useParams, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks";
import { shallowEqual } from "react-redux";
import {
  fetchCreatorBriefByIdThunk,
  deleteCreatorBriefThunk,
  updateCreatorBriefThunk,
  downloadCreatorBrief,
} from "../../stores/thunks/creatorBriefsThunk";
import { resetSelectedBrief } from "../../stores/slice/creatorBriefsSlice";
import { displayAlert } from "../../stores/slice/alertSlice.tsx";

import {
  RevActionsHeader,
  RevAvatar,
  RevBadge,
  RevButton,
  RevCard,
  RevDialog,
  RevIconButton,
  RevInput,
  RevLink,
  RevSpinner,
  RevTabs,
  RevTooltip,
} from "@revmatics/catalyst";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faUsers,
  faUser,
  faArrowLeft,
  faInfoCircle,
  faChevronRight,
  faMegaphone,
  faClock,
  faStars,
} from "@fortawesome/pro-light-svg-icons";
import {
  faCaretDown,
  faCaretRight,
  faPen,
} from "@fortawesome/pro-solid-svg-icons";
import { fullFormatNumbers } from "../../helpers/functions/formatNumber";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate";
import { BriefDeliverablesTab } from "./components/BriefDeliverablesTab";
import { BriefDetailsTab } from "./components/briefDetailsTab/BriefDetailsTab.tsx";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import { DeleteBriefDialog } from "./components/DeleteBriefDialog";
import { NotFoundPage } from "../notFound/NotFoundPage.tsx";
import { ContactInfoSection } from "./components/ContactInfoSection.tsx";
import { DownloadCreatorBriefsParamsSlug } from "../../stores/types/creatorBriefsTypes.tsx";

export const CreatorBriefDetailPage = () => {
  const {
    personaDetails: ffPersonaDetails,
    creatorContacts: ffCreatorContacts,
    detailsCampaign: ffDetailsCampaign,
    showCroLinks: ffShowCroLinks,
  } = useFlags();

  const { creatorBriefId } = useParams<{ creatorBriefId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("deliverables");

  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isOpenEditBriefTitle, setIsOpenEditBriefTitle] = useState(false);

  const { brief, isLoading, error, isDeleting, isDownloading } = useAppSelector(
    (state) => ({
      brief: state.creatorBriefs.selectedBrief,
      isLoading: state.creatorBriefs.isLoading,
      error: state.creatorBriefs.error,
      isDeleting: state.creatorBriefs.isDeleting,
      isDownloading: state.creatorBriefs.isDownloading,
    }),
    shallowEqual,
  );

  const [briefTitle, setBriefTitle] = useState(brief?.title);
  const currentBrand = useAppSelector((state) => state.brand.currentBrand);

  useEffect(() => {
    if (creatorBriefId && (!brief || brief.id.toString() !== creatorBriefId)) {
      dispatch(fetchCreatorBriefByIdThunk(creatorBriefId));
    }
  }, [dispatch, creatorBriefId, brief, currentBrand]);

  useEffect(() => {
    return () => {
      dispatch(resetSelectedBrief());
    };
  }, [dispatch]);

  useEffect(() => {
    if (brief) setBriefTitle(brief.title);
  }, [brief]);

  const handleConfirmDelete = () => {
    if (!brief) return;
    dispatch(deleteCreatorBriefThunk(brief.id))
      .unwrap()
      .then(() => {
        setIsDeleteDialogOpen(false);
        navigate("/creator-briefs");
      })
      .catch((err) => console.error("Failed to delete creator brief:", err));
  };

  const handleEditTitle = () => {
    if (brief && briefTitle && briefTitle !== brief.title) {
      dispatch(
        updateCreatorBriefThunk({ id: brief.id, data: { title: briefTitle } }),
      )
        .unwrap()
        .then(() => {
          setIsOpenEditBriefTitle(false);
        })
        .catch((err) => {
          console.error("Failed to update brief title", err);
          dispatch(
            displayAlert({
              type: "error",
              text: "Failed to update creator brief.",
            }),
          );
        });
    }
    setIsOpenEditBriefTitle(false);
  };

  const handleDownload = (slug: DownloadCreatorBriefsParamsSlug) => {
    if (creatorBriefId) {
      dispatch(
        downloadCreatorBrief({
          id: creatorBriefId,
          name: brief?.title,
          params: { format: slug },
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <div className="h-full">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if ((error || !brief) && !isLoading) {
    return <NotFoundPage />;
  }

  const tabData = [
    { id: "deliverables", title: "Deliverables" },
    { id: "details", title: "Details" },
  ];

  const infoDialogActions = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={() => setIsInfoDialogOpen(false)}
    />,
    <RevLink
      key="contact"
      pathname="/contact-us"
      text="Contact Us"
      displayAs="button"
      buttonSize="medium"
      buttonVariant="primary"
    />,
  ];

  if (brief && !error && !isLoading) {
    return (
      <>
        <div className="w-full">
          <RevActionsHeader
            title={brief.title}
            headerStatusBadge={
              <RevIconButton
                icon={faPen}
                handleClick={() => setIsOpenEditBriefTitle(true)}
              />
            }
            subtitle={
              <div className="flex items-center gap-1.5 text-monochrome-600 typography-body-sm">
                <FontAwesomeIcon icon={faClock} className="text-md" />
                <span>
                  Last Updated: {formatDateToMonthDayYear(brief.updated_at)}
                </span>
              </div>
            }
            backButton={
              <RevLink
                pathname="/creator-briefs"
                displayAs="button"
                buttonVariant="icon"
                buttonSize="icon"
                buttonColor="inherit"
                iconLeft={faArrowLeft}
                className="min-w-[40px]"
              />
            }
          >
            <Menu
              as="div"
              className="relative w-full md:w-auto inline-block text-left"
            >
              <MenuButton
                as={RevButton}
                text="Actions"
                buttonSize="medium"
                variant="primary"
                iconRight={faCaretDown}
                className="w-full md:w-auto"
                isLoading={isDownloading}
              />
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute md:right-0 w-[180px] origin-top-right rounded-md bg-white py-1 text-sm shadow-lg focus:outline-none z-10">
                  <MenuItem>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => setIsInfoDialogOpen(true)}
                        className={`${
                          active
                            ? "bg-monochrome-50 text-monochrome-900"
                            : "text-monochrome-700"
                        } group flex w-full items-center px-2 py-2 text-sm`}
                      >
                        Edit Deliverables
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => handleDownload("doc")}
                        className={`${
                          active
                            ? "bg-monochrome-50 text-monochrome-900"
                            : "text-monochrome-700"
                        } group flex w-full items-center px-2 py-2 text-sm`}
                      >
                        Download Doc
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => handleDownload("pdf")}
                        className={`${
                          active
                            ? "bg-monochrome-50 text-monochrome-900"
                            : "text-monochrome-700"
                        } group flex w-full items-center px-2 py-2 text-sm`}
                      >
                        Download PDF
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className={`${
                          active ? "bg-red-50 text-red-700" : "text-red-500"
                        } group flex w-full items-center px-2 py-2 text-sm`}
                      >
                        Delete
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </RevActionsHeader>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <RevCard title="Overview" paddingTitle="px-6 py-4">
                <div className="bg-primary-50 rounded-lg p-4 flex flex-col items-center">
                  <RevAvatar
                    variant="circle"
                    size="extraLarge"
                    image={
                      brief.creator?.creator_image_link ||
                      brief.creator?.creator_image_file ||
                      undefined
                    }
                    firstName={brief.creator.name.split(" ")[0]}
                    lastName={brief.creator.name.split(" ").pop()}
                    theme="light"
                  />
                  <p className="typography-body-bold-md mt-2 mb-4">
                    {brief.creator?.name ?? "N/A"}
                  </p>

                  <hr className="w-full border-t border-primary-600/20 mb-4" />

                  {brief.creator?.id && ffCreatorContacts && (
                    <ContactInfoSection creatorId={brief.creator.id} />
                  )}

                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 sm:gap-y-3">
                    <div className="flex items-start gap-2 text-monochrome-600 typography-body-sm">
                      <FontAwesomeIcon
                        icon={faGlobe}
                        className="text-md text-primary-800/50 mt-0.5"
                      />
                      <span>Total Size</span>
                    </div>
                    <div className="text-left typography-body-sm text-monochrome-900">
                      ≈
                      {fullFormatNumbers({
                        value: brief.creator?.total_community_size ?? 0,
                        noFormatNumber: true,
                        shortenValue: 1e3,
                      })}
                    </div>

                    <div className="flex items-start gap-2 text-monochrome-600 typography-body-sm">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-md text-primary-800/50 mt-0.5"
                      />
                      <span>Communities</span>
                    </div>
                    <div className="text-left typography-body-sm text-monochrome-900">
                      {[
                        ...new Set(
                          (brief.communities ?? []).map(
                            (community) => community.name,
                          ),
                        ),
                      ].join(", ")}
                    </div>

                    <div className="flex items-start gap-2 text-monochrome-600 typography-body-sm">
                      <FontAwesomeIcon
                        icon={faMegaphone}
                        className="text-md text-primary-800/50 mt-0.5"
                      />
                      <span>Campaign</span>
                    </div>
                    <div className="text-left min-w-0">
                      <div className="truncate">
                        {ffDetailsCampaign ? (
                          <RevLink
                            search={`?campaignId=${brief.campaign?.id}`}
                            pathname=""
                            text={brief.campaign?.name ?? "N/A"}
                            underline={false}
                          />
                        ) : (
                          <span>{brief.campaign?.name ?? "N/A"}</span>
                        )}
                      </div>
                      <p className="typography-body-xs text-monochrome-500">
                        Launch Date:{" "}
                        {formatDateToMonthDayYear(brief.campaign?.start_date)}
                      </p>
                    </div>

                    <div className="flex items-start gap-2 text-monochrome-600 typography-body-sm">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-md text-primary-800/50 mt-0.5"
                      />
                      <span>Persona</span>
                    </div>
                    <div className="text-left min-w-0">
                      <div className="flex items-center gap-1">
                        {ffPersonaDetails ? (
                          <RevLink
                            pathname=""
                            search={`personaId=${brief.persona?.id}`}
                            text={brief.persona?.name ?? "N/A"}
                            underline={false}
                          />
                        ) : (
                          <span className="typography-body-sm">
                            {brief.persona?.name ?? "N/A"}
                          </span>
                        )}
                        {brief.persona.is_ai_generated && (
                          <RevTooltip
                            position="top"
                            className="w-[220px]"
                            content="This persona was automatically generated by our platform."
                          >
                            <FontAwesomeIcon
                              icon={faStars}
                              className="typography-body-md text-purple-600 ml-1"
                            />
                          </RevTooltip>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {ffShowCroLinks && (
                  <div className="mt-6 border border-monochrome-200 rounded-lg">
                    <Disclosure>
                      {({ open }) => (
                        <div>
                          <DisclosureButton className="w-full p-3 flex justify-between items-center">
                            <div className="flex justify-start gap-2 items-center">
                              <p className="typography-body-bold-sm">
                                Landing Pages
                              </p>
                              <RevBadge
                                text={(
                                  brief.creator?.landing_pages?.length ?? 0
                                ).toString()}
                                color="blue"
                                variant="light"
                              />
                            </div>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className={`transition-transform duration-200 text-monochrome-600 ${
                                open ? "rotate-90" : ""
                              }`}
                            />
                          </DisclosureButton>

                          <DisclosurePanel className="p-3">
                            {(brief.creator?.landing_pages ?? []).length >
                              0 && (
                              <div className="space-y-2 mb-3">
                                {brief.creator.landing_pages.map(
                                  (page, index: number) => (
                                    <RevLink
                                      key={page.id || index}
                                      pathname={page.url}
                                      text={page.title}
                                    />
                                  ),
                                )}
                              </div>
                            )}
                            <RevLink
                              pathname={`${import.meta.env.VITE_CRO_API_URL}/funnels`}
                              text="Generate Landing Page"
                              displayAs="button"
                              buttonVariant="secondary"
                              className="w-full"
                              local={false}
                              target="_self"
                            />
                          </DisclosurePanel>
                        </div>
                      )}
                    </Disclosure>
                  </div>
                )}

                <div className="mt-6 p-3 border border-primary-300 bg-primary-25 rounded-lg text-primary-600">
                  <div className="flex gap-3 items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-xl" />
                    <p className="typography-body-bold-sm">
                      The Purpose of this Creator Brief
                    </p>
                  </div>
                  <hr className="w-full border-t border-primary-300 my-2" />
                  <Disclosure>
                    {({ open }) => (
                      <div>
                        <DisclosureButton className="flex items-center gap-2 px-1 text-primary-500 hover:text-primary-600">
                          <FontAwesomeIcon
                            icon={faCaretRight}
                            className={`transition-transform duration-200 text-primary-600 text-md ${
                              open ? "rotate-90" : ""
                            }`}
                          />
                          <span className="typography-body-sm text-primary-600">
                            {open ? "Hide details" : "Show details"}
                          </span>
                        </DisclosureButton>
                        <DisclosurePanel className="mt-2">
                          <p className="typography-body-sm text-primary-900">
                            {brief.objective ||
                              "No purpose specified for this brief."}
                          </p>
                        </DisclosurePanel>
                      </div>
                    )}
                  </Disclosure>
                </div>
              </RevCard>
            </div>

            <div className="lg:col-span-2">
              <div className="mb-4">
                <RevTabs
                  data={tabData}
                  selected={activeTab}
                  handleSelect={setActiveTab}
                  size="medium"
                  tabId="brief-detail-tabs"
                />
              </div>

              {activeTab === "deliverables" && (
                <BriefDeliverablesTab brief={brief} />
              )}
              {activeTab === "details" && <BriefDetailsTab brief={brief} />}
            </div>
          </div>
        </div>

        <DeleteBriefDialog
          isOpen={isDeleteDialogOpen}
          setIsOpen={setIsDeleteDialogOpen}
          brief={brief}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
        />

        <RevDialog
          isOpen={isInfoDialogOpen}
          setIsOpen={setIsInfoDialogOpen}
          title="Edit Deliverables"
          variant="info"
          btnList={infoDialogActions}
        >
          <p className="text-center">
            Sorry, you're not able to edit the creator brief deliverables at
            this time. If you need to make changes, please contact
            support@revmatics.ai so a Revmatics specialist can help you.
          </p>
        </RevDialog>
        <RevDialog
          isOpen={isOpenEditBriefTitle}
          title="Edit Creator Brief Name"
          setIsOpen={() => setIsOpenEditBriefTitle(false)}
          btnList={[
            <RevButton
              text="Cancel"
              variant="secondary"
              handleClick={() => setIsOpenEditBriefTitle(false)}
              key="brief-cancel-title-edit"
            />,
            <RevButton
              text="Save"
              variant="primary"
              handleClick={handleEditTitle}
              isLoading={isLoading}
              key="brief-save-title-edit"
            />,
          ]}
        >
          <RevInput
            name="title"
            type="text"
            label="Brief Title"
            size="md"
            defaultValue={brief.title}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setBriefTitle(e.target.value)
            }
          />
        </RevDialog>
      </>
    );
  }
  return null;
};
